<template>
    <ElementLayout>
        <template #element>
            <AtlasAlert variant="warning" v-if="Number(rebuildCost) > Number(maximumRebuildCost)">
                We've noticed the rebuild cost is significantly higher than the BCIS estimate, please check and amend if required. If this is correct please continue.
            </AtlasAlert>
            <div class="border rounded p-20" style="position: relative;">
                <!-- Arrow pointing to the input field -->
                <div style="position: absolute; top: -10px; left: 30px; width: 0; height: 0; border-left: 10px solid transparent; border-right: 10px solid transparent; border-bottom: 10px solid #dee2e6;"></div>
                <div style="position: absolute; top: -9px; left: 31px; width: 0; height: 0; border-left: 9px solid transparent; border-right: 9px solid transparent; border-bottom: 9px solid #fff;"></div>

                <div class="d-flex mb-4">
                    <div class="col-12 col-md-8">
                        <p>
                            The rebuild cost of your property will typically be less than its market value as the rebuild cost does not include the value of the land.
                        </p>
                        <p>
                            BCIS has estimated that your property's rebuild cost is approximately <strong>£{{ Number(minimumRebuildCost).formatMoney() }}</strong> if built of <strong>brick</strong>, and <strong>£{{ Number(maximumRebuildCost).formatMoney() }}</strong> if built of <strong>stone</strong>.
                        </p>
                        <p>
                            This value could vary dependent on the size of your house in comparison to an average property, the quantity and quality of fittings, and any special or unusual features.
                        </p>
                    </div>
                
                    <div class="border rounded p-10 col-12 col-md-4">
                            <p>
                                <strong>How is this calculated?</strong>
                            </p>
                                
                            <p>
                                <a href="https://abi.bcis.co.uk/" target="_blank">BCIS</a> have calculated this value based on your property's details:
                            </p>

                            <ul class="font-weight-bold">
                                <li v-if="propertyType">{{ propertyType }}</li>
                                <li v-if="yearBuilt">Built in {{ yearBuilt }}</li>
                                <li v-if="riskPostcode">Located in {{ riskPostcode }}</li>
                                <li v-if="numberOfBedrooms">{{ numberOfBedrooms }} bedrooms</li>
                                <li v-if="numberOfFlats">{{ numberOfFlats }} flats</li>
                                <li v-if="numberOfRooms">{{ numberOfRooms }} rooms</li>
                                <li v-if="numberOfGarages">{{ numberOfGarages }} garages</li>
                                <li v-if="numberOfBathrooms">{{ numberOfBathrooms }} bathrooms</li>
                            </ul>
                    </div>
                </div>
            
                <hr />

                <p class="fs-12 mt-4">
                    The data provided is owned and supplied under licence by BCIS the Building Cost Information Service of RICS. This rebuilding cost estimate is based on minimal details about your home. If you have a listed property or one with special or unusual features, you should obtain professional advice on the rebuilding cost. It is your responsibility to provide an accurate rebuilding sum insured for your home, but as a guide to assist you then you may wish to consider the estimated figures provided by the BCIS. You may also wish to refer to the advice offered by the Association of British Insurers (ABI) on checking the rebuilding cost of your home at <a href="https://abi.bcis.co.uk/" target="_blank">BCIS</a>.
                </p>
    
            </div>
        </template>

        <template v-for="(component, slot) in elementSlots" #[slot]>
            <slot :name="slot" :el$="el$">
                <component :is="component" :el$="el$" />
            </slot>
        </template>
    </ElementLayout>
</template>

<script>
import AtlasAlert from "@/components/Common/AtlasAlert.vue"
import { defineElement } from '@vueform/vueform'
import { computed, inject } from 'vue'

export default defineElement({
    name: "BcisCalculatorInfoElement",

    components: { AtlasAlert },

    props: {
        riskPostcode: { default: null },
        yearBuilt: { default: null },
        propertyType: { default: null },
        numberOfBedrooms: { default: null },
        numberOfFlats: { default: null },
        numberOfRooms: { default: null },
        numberOfGarages: { default: null },
        numberOfBathrooms: { default: null },
        rebuildCost: { default: null },
        minimumRebuildCost: { default: null },
        maximumRebuildCost: { default: null },
    },

    setup(props) {
        const vueformApi = inject('vueformApi', null)
        const resolve = (val) => typeof val === 'function' ? val(vueformApi?.value) : val

        return {
            riskPostcode: computed(() => resolve(props.riskPostcode)),
            yearBuilt: computed(() => resolve(props.yearBuilt)),
            propertyType: computed(() => resolve(props.propertyType)),
            numberOfBedrooms: computed(() => resolve(props.numberOfBedrooms)),
            numberOfFlats: computed(() => resolve(props.numberOfFlats)),
            numberOfRooms: computed(() => resolve(props.numberOfRooms)),
            numberOfGarages: computed(() => resolve(props.numberOfGarages)),
            numberOfBathrooms: computed(() => resolve(props.numberOfBathrooms)),
            rebuildCost: computed(() => {
                const value = resolve(props.rebuildCost)
                if (value == null || value === '') return null
                const parsed = Number(String(value).replace(/,/g, ''))
                return Number.isNaN(parsed) ? null : parsed
            }),
            minimumRebuildCost: computed(() => resolve(props.minimumRebuildCost)),
            maximumRebuildCost: computed(() => resolve(props.maximumRebuildCost)),
        }
    }
});
</script>
